<script lang="ts" setup>
  import { z } from 'zod'
  import type { FetchError } from 'ofetch'
  import Select from 'primevue/select'
  import { nameRegex } from '@/constants/regex'
  import { GRILLD_TERMS_GIFT_CARD } from '@/constants/url'
  import type { GiftCardOrderResponse } from '@/types/api/gift-cards'
  import type { GiftCardPersonalisationValues } from '@/types/stores/gift-cards'
  import { useSubmitForm } from '../../composables/submit.composable'
  import type { PersonaliseSubmitOrderParams } from '../../types/submit'
  import type { EmitNextStepPayload } from '../../types/wizard'

  const giftCardStore = useGiftCardsStore()

  const emit = defineEmits<{
    (event: 'next-step', payload: EmitNextStepPayload): void
  }>()

  const validationSchema = toTypedSchema(
    z.object({
      presetAmount: z.number().optional(),
      customAmount: z
        .number()
        .min(20, 'Amount must be between $20 and $1000')
        .max(1000, 'Amount must be between $20 and $1000')
        .nullable()
        .optional()
        .superRefine((arg, ctx) => {
          // If custom amount is missing, then preset amount is required
          if (!giftCardStore.wizard.values.personalisation.presetAmount && !arg) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Select or enter a gift card value'
            })
            return z.NEVER
          }
        }),
      quantity: z.number().min(1).max(50),
      purchaserGivenName: z
        .string()
        .regex(nameRegex, 'First name must only contain letters and spaces')
        .min(1, 'Required')
        .max(64),
      purchaserFamilyName: z
        .string()
        .regex(nameRegex, 'Last name must only contain letters and spaces')
        .min(1, 'Required')
        .max(64),
      purchaserEmail: z.string().email('Your email must be valid and contain an @ in the address'),
      scheduledDateTime: z
        .string()
        .optional()
        .nullish()
        .superRefine((arg, ctx) => {
          // If scheduled is disabled, then schedule date time is not required
          if (!giftCardStore.wizard.scheduled) {
            return z.NEVER
          }

          // Minimum schedule date time is 1 hour from now, in Melbourne time, however we do not
          // know the time offset between local time and Melbourne time, so we will allow the user
          // to select any time on the current day
          const minDateTime = new Date(new Date().toDateString())

          // Maximum schedule date time is 30 days from now, in Melbourne time, however we do not
          // know the time offset between local time and Melbourne time, so we will allow the user
          // to select any time up to 23:59, 30 days from today
          const maxDateTime = new Date(new Date(new Date().toDateString()).getTime() + 31 * 24 * 60 * 60 * 1000 - 1)

          // Required
          if (!arg) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Schedule date time is required'
            })

            return z.NEVER
          }

          // Minimum date time
          if (new Date(arg) < minDateTime) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Schedule date time cannot be in the past'
            })

            return z.NEVER
          }

          // Maximum date time
          if (new Date(arg) > maxDateTime) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Schedule date time must be within 30 days from now'
            })

            return z.NEVER
          }
        })
    })
  )

  const {
    defineField,
    handleSubmit,
    validateField,
    setValues: setFormValues,
    values: formValues,
    meta,
    isSubmitting,
    errors
  } = useForm({
    validationSchema,
    initialValues: giftCardStore.wizard.values.personalisation
  })

  const isValid = computed(() => meta.value.valid)
  const recipientsMessage = computed(() => {
    if (isForRecipientEnabled.value) {
      return 'You will add recipients in the next stage.'
    }
    const subject = formValues.quantity && formValues.quantity > 1 ? 'Gift cards' : 'Gift card'
    if (formValues.purchaserEmail) {
      return `${subject} will be sent to ${formValues.purchaserEmail}`
    }
    return `${subject} will be sent to you`
  })

  /* FORM: All fields */

  // Update the store when the form values change
  watch(formValues, (newValues) => {
    // Find the value updated in the form and update the store
    const updatedField = Object.keys(newValues).find(
      (key) =>
        newValues[key as keyof typeof formValues] !==
        giftCardStore.wizard.values.personalisation[key as keyof GiftCardPersonalisationValues]
    )

    if (!updatedField) {
      return
    }

    giftCardStore.wizard.values.personalisation = {
      ...giftCardStore.wizard.values.personalisation,
      [updatedField]: newValues[updatedField as keyof GiftCardPersonalisationValues]
    }
  })

  /* FORM: Preset Amount */

  const [presetAmount] = defineField('presetAmount')

  // If a presetAmount is selected, clear the customAmount field
  watch(
    () => formValues.presetAmount,
    (presetAmount) => {
      if (presetAmount && formValues.customAmount) {
        setFormValues({ customAmount: undefined }, false)
      }
    }
  )

  /* FORM: Gift Card Quantity */

  const [quantityFormValue] = defineField('quantity')
  const quantityInput = computed({
    get: () => ({
      value: quantityFormValue.value,
      label: quantityFormValue.value
    }),
    set: (newValue: { value: number; label: string }) => {
      quantityFormValue.value = newValue.value
    }
  })

  // If the quantity has changed (e.g. from this step), update the recipients array
  watch(
    () => formValues.quantity,
    (newQuantity) => {
      if (newQuantity && newQuantity >= giftCardStore.wizard.values.recipients.recipients.length) {
        // Push new recipients to the recipients array if the new quantity is greater than the current recipients length
        // Prevents the user from losing any recipients they have already added
        giftCardStore.wizard.values.recipients.recipients = [
          ...giftCardStore.wizard.values.recipients.recipients,
          ...Array.from({ length: newQuantity - giftCardStore.wizard.values.recipients.recipients.length }, () => ({
            name: '',
            emailAddress: ''
          }))
        ]
      }
    }
  )

  // If the quantity has changed (e.g. from Recipients Step), update the form value
  watch(
    () => giftCardStore.wizard.values.personalisation.quantity,
    (quantity) => {
      if (!quantity || quantity === formValues.quantity) {
        return
      }

      setFormValues({
        quantity
      })
    }
  )

  /* UI controls for sections in form */
  const isForRecipientEnabled = computed({
    get: () => giftCardStore.wizard.target === 'recipient',
    set: (value) => {
      giftCardStore.wizard.target = value ? 'recipient' : 'purchaser'
    }
  })

  const panelRecipientRef = ref<HTMLDivElement | null>(null)
  const panelScheduledRef = ref<HTMLDivElement | null>(null)
  const imageKey = ref(0)

  onMounted(() => {
    // Reload the image when the component is mounted to avoid the image being left in a loading state
    // when the user navigates back to this step
    imageKey.value++
  })

  watch(
    () => giftCardStore.wizard.scheduled,
    (isScheduledToggleOn) => {
      if (isScheduledToggleOn) {
        panelScheduledRef.value?.scrollIntoView({ behavior: 'smooth' })
      } else {
        validateField('scheduledDateTime')
      }
    }
  )

  const isAlertVisible = ref(false)
  const alertContent = ref({
    header: "Oops! That's an error.",
    message: 'Please check your internet connection and try again.'
  })

  const { executeRecaptcha } = useGoogleRecaptcha()

  function onSubmit() {
    handleSubmit(async () => {
      if (!isValid.value) {
        console.error('Form is invalid. Not submitting.')
        return
      }

      if (isForRecipientEnabled.value) {
        // Move to step 3 (Recipients Step)
        emit('next-step', { step: 'recipients' })

        return
      }

      try {
        const validatedBody = giftCardStore.getBodyForSubmissionOnPersonaliseStep()

        if (!validatedBody) {
          throw Error('Body is not valid is not defined')
        }

        const body: PersonaliseSubmitOrderParams['body'] = {
          ...validatedBody,
          recaptchaResponse: (await executeRecaptcha('submit')) || ''
        }

        const response = await useSubmitForm({
          step: 'personalise',
          body
        })

        if (!response) {
          throw Error('Failed to submit form')
        }

        // Move to step 4 (Payment Step)
        emit('next-step', { step: 'payment', paymentDetails: response })
      } catch (error: unknown) {
        console.log(error as FetchError<GiftCardOrderResponse>)
        isAlertVisible.value = true
      }
    })()
  }

  /**
   * Reset the preset amount when the user types in the custom amount field
   */
  function onCustomAmountInput() {
    if (formValues.presetAmount) {
      setFormValues({ presetAmount: undefined }, false)
    }
  }
</script>

<template>
  <div class="c-gift-card-personalise gift-card-step-panel">
    <div class="step-header-content">
      <span class="text-button-medium">Grill'd gift cards</span>
      <h2 class="header-3">A Gift to Savour</h2>
    </div>

    <div class="body-content">
      <div class="col-1">
        <div class="gift-card-image-block">
          <CLazyImage
            v-if="giftCardStore.wizard.values.skin?.url"
            :key="imageKey"
            :src="giftCardStore.wizard.values.skin.url"
            alt=""
            aspect-ratio="1.8"
            border-radius="4px"
            fit="cover"
          />
          <p class="body">
            Email a gift card with a personal message. Valid for 3 years, redeemable online or at Grill’d restaurants.
          </p>
        </div>
      </div>

      <div class="col-2">
        <form @submit.prevent="onSubmit">
          <fieldset class="form-group">
            <div class="form-group-header mb-1">
              <h2 class="header-5">Extra value bonus bucks!</h2>
              <p class="body">
                Purchase one of the below values and we’ll throw in a bit extra. Cos we’re nice like that.
              </p>
            </div>

            <CBlockRadioButtonGroup
              v-model="presetAmount"
              :options="[
                { label: '$200', value: 20000, message: 'You pay $190' },
                { label: '$500', value: 50000, message: 'You pay $450' }
              ]"
            />

            <div class="separator">or</div>

            <CInputNumberField
              label="Custom Amount"
              name="customAmount"
              prefix="$"
              validate-initial-value
              @input="onCustomAmountInput"
            />
            <p class="disclaimer body-small">Minimum amount $20. Custom amounts do not provide any bonus bucks.</p>
          </fieldset>

          <fieldset>
            <div class="form-group-header">
              <h2 class="header-5">How many gift cards?</h2>
            </div>

            <div class="c-select-field">
              <Select
                v-model="quantityInput"
                :options="Array.from({ length: 50 }, (_, i) => ({ label: i + 1, value: i + 1 }))"
                :pt="{
                  root: {
                    class: 'custom-select'
                  }
                }"
                :virtual-scroller-options="{ itemSize: 38 }"
                aria-labelledby="inputLabel"
                fluid
                name="giftCardQuantity"
                option-label="label"
                size="large"
              />
              <small v-if="errors.quantity" id="giftCardQuantity-error" class="input-error-message">
                {{ errors.quantity }}
              </small>
            </div>
          </fieldset>

          <fieldset class="form-group">
            <div class="form-group-header">
              <h2 class="header-5">Your details</h2>
            </div>
            <CInputTextField :maxlength="64" label="First Name" name="purchaserGivenName" validate-initial-value />

            <CInputTextField :maxlength="64" label="Last Name" name="purchaserFamilyName" validate-initial-value />

            <CInputTextField :maxlength="254" label="Email" name="purchaserEmail" type="email" validate-initial-value />
          </fieldset>

          <div class="form-group">
            <div class="form-group-header">
              <div class="form-group-header--row">
                <h2 class="header-5">Send to someone else</h2>

                <ToggleSwitch v-model="isForRecipientEnabled" />
              </div>
            </div>

            <div ref="panelRecipientRef" :class="['form-group-body', 'panel', 'panel--open']">
              <CAlert variant="note" class="recipient-alert">{{ recipientsMessage }}</CAlert>
            </div>
          </div>

          <fieldset class="form-group">
            <div class="form-group-header">
              <div class="form-group-header--row">
                <h2 class="header-5">Send later</h2>
                <ToggleSwitch v-model="giftCardStore.wizard.scheduled" />
              </div>
            </div>

            <div
              ref="panelScheduledRef"
              :class="['form-group-body', 'panel', { 'panel--open': giftCardStore.wizard.scheduled }]"
            >
              <p class="body mb-1">Surprise them at just the right moment.</p>

              <client-only>
                <CDatePickerField
                  :disabled="!giftCardStore.wizard.scheduled"
                  :max-date="new Date(new Date(new Date().toDateString()).getTime() + 31 * 24 * 60 * 60 * 1000 - 1)"
                  :min-date="new Date(new Date().toDateString())"
                  helper-text="Sydney/Melbourne Time"
                  label="Schedule Time"
                  name="scheduledDateTime"
                  validate-initial-value
                />
              </client-only>
            </div>
          </fieldset>

          <p class="disclaimer body-small">
            Gift cards can be redeemed online or in restaurant. Gift cards cannot be redeemed for catering orders. 'Send
            Later' scheduled date and time cannot be changed after purchase.
          </p>

          <div>
            <CLoadingButton :disabled="!isValid" :loading="isSubmitting" fluid type="submit" @click="onSubmit">
              Confirm
            </CLoadingButton>

            <p class="terms-and-conditions-text body-small">
              Valid for 3 years.
              <a :href="GRILLD_TERMS_GIFT_CARD" target="_blank">Full Terms & Conditions</a>
            </p>
          </div>
        </form>
      </div>
    </div>
  </div>

  <Dialog v-model:visible="isAlertVisible" :closable="false" :draggable="false" :header="alertContent.header" modal>
    <p class="body">
      {{ alertContent.message }}
    </p>
    <template #footer>
      <CButton fluid variant="primary" @click="isAlertVisible = false">Gotcha</CButton>
    </template>
  </Dialog>
</template>

<style lang="scss" scoped>
  @import '../../styles/step.scss';
  @import '@/components/form/styles/form.scss';

  // eslint-disable vue-scoped-css/no-unused-selector
  .custom-select {
    // Fix for the p-select-fluid class not being correctly defined for the PrimeVue Select component
    // Can be removed when the fis is released in PrimeVue (> 4.2.4)
    width: 100%;
  }

  .recipient-alert {
    word-break: break-word;
  }
</style>
